import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, User, ArrowRight, Activity, Link2, Hexagon } from 'lucide-react';
import { motion } from 'framer-motion';
import { SceneBackground } from '../components/ui/SceneBackground';
import { generateProfilePageHTML } from '../utils/profilePageTemplate';
import { HowItWorks } from '../components/sections/HowItWorks';
import { AmaiaSection } from '../components/sections/AmaiaSection';
import { PassportExplainer } from '../components/sections/PassportExplainer';
import { Ecosystem } from '../components/sections/Ecosystem';
import { CTASection } from '../components/sections/CTASection';
import LandingHeader from '../components/layout/LandingHeader';
import LandingFooter from '../components/layout/LandingFooter';

// ===== MAIN HOME COMPONENT =====

const Home: React.FC = () => {
  const passportPreviewHtml = generateProfilePageHTML({
    joinDate: Date.now() - 1000 * 60 * 60 * 24 * 365,
    xPostingsCount: 210,
    githubInteractionsCount: 85,
    userId: 'Clement',
    processId: '4c3e1ab9b12a7f88',
    points: 8420,
    currentPrimaryName: 'Clement',
    avatarUrl: 'https://unavatar.io/x/premiumtrendz',
    xId: 'Clement',
    githubId: 'Clement',
    levelTitle: 'Trusted Builder',
    level: 8,
    pointsBreakdown: { github: 1240, x: 980, ai: 760, gm: 420, gn: 220, sendit: 180, role: 120 }
  }, { transparent: true });
  
  const passportPreviewHtmlClean = passportPreviewHtml
    .replace(/<body[^>]*>/i, '<body style="margin:0;background:transparent;">')
    .replace(/<\/head>/i, '<style>body{background:transparent !important;}</style></head>');

  return (
    <SceneBackground>
      <LandingHeader />
      
      {/* Skip to content for a11y */}
      <a 
        href="#main-content" 
        className="skip-to-content focus:outline-none"
        style={{
          position: 'absolute',
          top: '-100px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '0.75rem 1.5rem',
          background: '#00ff9d',
          color: '#06190e',
          fontWeight: 700,
          borderRadius: '9999px',
          zIndex: 9999,
          transition: 'top 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          textDecoration: 'none',
          boxShadow: '0 4px 20px rgba(0, 255, 157, 0.4)'
        }}
        onFocus={(e) => e.currentTarget.style.top = '1.5rem'}
        onBlur={(e) => e.currentTarget.style.top = '-100px'}
      >
        Skip to main content
      </a>

      <div className="min-h-screen text-white font-sans selection:bg-[#00ff9d]/30 selection:text-green-100">
        
        {/* ===== HERO SECTION ===== */}
        <section id="main-content" className="relative min-h-screen flex items-center px-6 pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
          
          {/* Cinematic lighting - Left side text glow */}
          <div className="absolute top-[20%] -left-[10%] w-[800px] h-[800px] bg-gradient-to-tr from-[#00ff9d]/10 via-[#00ff9d]/5 to-transparent blur-[120px] pointer-events-none rounded-full" />

          <div className="max-w-7xl mx-auto w-full relative z-10">
            <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-20 items-center">
              
              {/* Left: Narrative */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="relative z-20"
              >

                {/* Main Headline */}
                <h1
                  className="text-5xl md:text-6xl lg:text-[5.5rem] font-bold leading-[1.08] tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-[#e2fff2] to-[#00ff9d]/60 drop-shadow-sm"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  The identity layer
                  <br />
                  your reputation deserves
                </h1>

                {/* Subheadline */}
                <p className="mt-8 text-lg md:text-xl text-white/60 leading-relaxed max-w-xl font-light">
                  Collect proof across wallets, socials, and protocols. Seal it into a <strong className="text-[#00ff9d] font-semibold tracking-wide drop-shadow-[0_0_10px_rgba(0,255,157,0.3)]">soulbound passport</strong> that travels with you.
                </p>

                {/* CTA Buttons */}
                <div className="mt-12 flex flex-col sm:flex-row gap-5 relative z-30">
                  <Link to="/amverify" className="group">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="relative w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-base bg-[#00ff9d] text-[#040606] transition-all flex items-center justify-center space-x-3 shadow-[0_0_20px_rgba(0,255,157,0.3)] hover:shadow-[0_0_40px_rgba(0,255,157,0.5)] overflow-hidden border border-[#00ff9d]"
                    >
                      {/* Diagonal light sweep */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 translate-x-[-150%] group-hover:translate-x-[150%] transition-transform duration-700 ease-out" />
                      
                      <Shield className="relative z-10 w-5 h-5" />
                      <span className="relative z-10 tracking-wide">Start verification</span>
                      <ArrowRight className="relative z-10 w-4 h-4 opacity-70 group-hover:translate-x-1 transition-transform" />
                    </motion.button>
                  </Link>
                  <Link to="/ampassport">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full sm:w-auto px-8 py-4 rounded-xl font-semibold text-base bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 hover:border-[#00ff9d]/40 text-white/90 backdrop-blur-xl transition-all flex items-center justify-center space-x-3 hover:shadow-[0_0_30px_rgba(0,255,157,0.1)]"
                    >
                      <User className="w-5 h-5 text-white/50 group-hover:text-[#00ff9d]/70 transition-colors" />
                      <span>View passport</span>
                    </motion.button>
                  </Link>
                </div>

                {/* Hardware Data Nodes */}
                <div className="mt-16 grid sm:grid-cols-2 gap-4 max-w-2xl">
                  <div className="group bg-gradient-to-b from-white/[0.04] to-transparent border border-white/5 hover:border-[#00ff9d]/30 rounded-2xl p-5 backdrop-blur-md transition-all duration-500 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/20 group-hover:via-[#00ff9d]/60 to-transparent transition-colors duration-500" />
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 group-hover:text-[#00ff9d]/90 mb-2 flex items-center gap-2 transition-colors duration-300">
                      <Activity className="w-3.5 h-3.5 text-[#00ff9d]" />
                      Status
                    </div>
                    <div className="text-sm font-semibold text-white/90 tracking-wide">Soulbound credential</div>
                  </div>
                  <div className="group bg-gradient-to-b from-white/[0.04] to-transparent border border-white/5 hover:border-[#00ff9d]/30 rounded-2xl p-5 backdrop-blur-md transition-all duration-500 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/20 group-hover:via-[#00ff9d]/60 to-transparent transition-colors duration-500" />
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 group-hover:text-[#00ff9d]/90 mb-2 flex items-center gap-2 transition-colors duration-300">
                      <Link2 className="w-3.5 h-3.5 text-[#00ff9d]" />
                      Network
                    </div>
                    <div className="text-sm font-semibold text-white/90 tracking-wide">Arweave • AO • ARIO</div>
                  </div>
                </div>
              </motion.div>

              {/* Right: True 3D Holographic Passport Rig */}
              <motion.div
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1.2, delay: 0.2, type: "spring", stiffness: 40, damping: 20 }}
                className="relative flex justify-center lg:justify-end mt-20 lg:mt-0 perspective-[2500px] pointer-events-none sm:pointer-events-auto"
              >
                <div className="relative w-full max-w-[440px] flex justify-center">
                  
                  {/* Floating 3D Rig Container */}
                  <motion.div
                    animate={{ 
                      y: [-12, 12, -12],
                      rotateY: [-6, 4, -6],
                      rotateX: [3, 8, 3]
                    }}
                    transition={{ 
                      duration: 9, 
                      repeat: Infinity, 
                      ease: "easeInOut" 
                    }}
                    style={{ transformStyle: 'preserve-3d' }}
                    className="relative w-full"
                  >
                    {/* Deep Z-axis background glow (sits way behind the card for volumetric light) */}
                    <motion.div
                      animate={{ opacity: [0.4, 0.6, 0.4], scale: [0.95, 1.05, 0.95] }}
                      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                      style={{ transform: 'translateZ(-150px)' }}
                      className="absolute inset-0 bg-[#00ff9d]/30 blur-[90px] rounded-[4rem]"
                    />

                    {/* Main Glass Card (Base Layer) */}
                    <div 
                      style={{ transform: 'translateZ(0px)' }}
                      className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-black/70 shadow-[0_30px_60px_rgba(0,0,0,0.6),inset_0_1px_1px_rgba(255,255,255,0.1)]"
                    >
                      {/* Fake Mac/OS Header Bar */}
                      <div className="h-10 bg-gradient-to-b from-white/[0.08] to-transparent border-b border-white/10 flex items-center px-5 gap-2.5">
                        <div className="w-3 h-3 rounded-full bg-white/20 hover:bg-red-500/80 transition-colors cursor-pointer" />
                        <div className="w-3 h-3 rounded-full bg-white/20 hover:bg-yellow-500/80 transition-colors cursor-pointer" />
                        <div className="w-3 h-3 rounded-full bg-white/20 hover:bg-[#00ff9d]/80 transition-colors cursor-pointer" />
                        <div className="ml-auto flex items-center gap-2.5 bg-black/40 px-3 py-1 rounded-full border border-white/5">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00ff9d] shadow-[0_0_8px_#00ff9d] animate-pulse" />
                          <div className="text-[9px] uppercase tracking-[0.2em] text-[#00ff9d]/80 font-bold">
                            Live Preview
                          </div>
                        </div>
                      </div>
                      
                      {/* Iframe Content */}
                      <div className="relative p-1 bg-gradient-to-b from-white/[0.02] to-transparent">
                        <iframe
                          title="amPassport preview"
                          srcDoc={passportPreviewHtmlClean}
                          className="w-full h-[680px]"
                          style={{ border: '0', pointerEvents: 'none', background: 'transparent', display: 'block' }}
                        />
                      </div>
                    </div>

                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ===== ALL SECTIONS ===== */}
        <div className="relative z-10">
          <HowItWorks />
          <PassportExplainer />
          <AmaiaSection />
          <Ecosystem />
          <CTASection />
        </div>

        <LandingFooter />
      </div>
    </SceneBackground>
  );
};

export default Home;
